/**
 * 前台通知 Hooks
 * 使用 TanStack Query 管理通知数据
 *
 * @see Requirements 2.1-2.6
 */

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import notificationService, {
  NotificationListParams,
  NotificationListData,
} from "@/services/notificationService";
import { useUserStore } from "@/stores/userStore";

// Query key 常量
const NOTIFICATIONS_QUERY_KEY = ["user", "notifications"] as const;

interface InfiniteNotificationsData {
  pages: NotificationListData[];
  pageParams: (number | undefined)[];
}

/**
 * 使用无限滚动获取通知列表
 * @see Requirements 2.1
 */
export function useUserNotifications(
  params: Omit<NotificationListParams, "page"> = {},
) {
  const { per_page = 20 } = params;

  return useInfiniteQuery({
    queryKey: [...NOTIFICATIONS_QUERY_KEY, params],
    queryFn: ({ pageParam = 1 }) =>
      notificationService.list({ ...params, page: pageParam, per_page }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.current_page < lastPage.meta.total_pages) {
        return lastPage.meta.current_page + 1;
      }
      return undefined;
    },
    // 缓存配置
    staleTime: 5 * 60 * 1000, // 5 分钟
    gcTime: 10 * 60 * 1000, // 10 分钟
  });
}

/**
 * 标记单条通知为已读
 * @see Requirements 2.2, 2.5, 2.6
 */
export function useMarkNotificationAsRead() {
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");
  const currentUser = useUserStore((state) => state.currentUser);
  const setUser = useUserStore((state) => state.setUser);

  return useMutation({
    mutationFn: (id: number) => notificationService.markAsRead(id),
    onSuccess: (_, variables) => {
      const notificationId = typeof variables === "number" ? variables : 0;

      // 优化：只更新缓存，不重新获取
      queryClient.setQueriesData<InfiniteNotificationsData>(
        { queryKey: NOTIFICATIONS_QUERY_KEY },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              notifications: page.notifications.map((notification) =>
                notification.id === notificationId
                  ? { ...notification, read_at: new Date().toISOString() }
                  : notification,
              ),
            })),
          };
        },
      );

      // 更新全局未读数量
      if (currentUser && currentUser.unread && currentUser.unread > 0) {
        setUser({
          ...currentUser,
          unread: Math.max(0, currentUser.unread - 1),
        });
      }

      toast.success(t("notifications.toast.markedRead"));
    },
    onError: () => {
      toast.error(t("notifications.toast.operationFailed"));
    },
  });
}

/**
 * 标记所有通知为已读
 * @see Requirements 2.3, 2.5, 2.6
 */
export function useMarkAllNotificationsAsRead() {
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");
  const currentUser = useUserStore((state) => state.currentUser);
  const setUser = useUserStore((state) => state.setUser);

  return useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      // 优化：更新缓存中的所有通知为已读
      queryClient.setQueriesData<InfiniteNotificationsData>(
        { queryKey: NOTIFICATIONS_QUERY_KEY },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              notifications: page.notifications.map((notification) => ({
                ...notification,
                read_at: new Date().toISOString(),
              })),
            })),
          };
        },
      );

      // 更新全局未读数量为 0
      if (currentUser) {
        setUser({ ...currentUser, unread: 0 });
      }

      toast.success(t("notifications.toast.allMarkedRead"));
    },
    onError: () => {
      toast.error(t("notifications.toast.operationFailed"));
    },
  });
}

/**
 * 删除通知
 * @see Requirements 2.4, 2.5
 */
export function useDeleteUserNotification() {
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");

  return useMutation({
    mutationFn: (id: number) => notificationService.delete(id),
    onSuccess: (_, variables) => {
      const notificationId = typeof variables === "number" ? variables : 0;

      // 优化：从缓存中移除通知，不重新获取
      queryClient.setQueriesData<InfiniteNotificationsData>(
        { queryKey: NOTIFICATIONS_QUERY_KEY },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              notifications: page.notifications.filter(
                (n) => n.id !== notificationId,
              ),
            })),
          };
        },
      );

      toast.success(t("notifications.toast.deleted"));
    },
    onError: () => {
      toast.error(t("notifications.toast.operationFailed"));
    },
  });
}

/**
 * 导出 query key 供外部使用
 */
export { NOTIFICATIONS_QUERY_KEY };
